import type { ModuleSearchResult } from '@/lib/api/search'
import { useEffect, useRef, useState } from 'react'
import { useTheme } from '@/components/theme-provider'
import { LinkedModulePicker } from '@/components/time-entry/LinkedModulePicker'
import { SplitTimeEntryDialog } from '@/components/time-entry/SplitTimeEntryDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useUpdateTimeEntryMutation } from '@/hooks/mutations/useTimeEntryMutations'
import { useOrganizationSettingsQuery } from '@/hooks/queries/useOrganizationSettingsQuery'
import { useTimerSessionEntriesQuery } from '@/hooks/queries/useTimeEntriesQuery'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAccountType } from '@/hooks/useAccountType'
import { useIsExternalUser } from '@/hooks/useIsExternalUser'
import { useResolvedDarkMode } from '@/hooks/useResolvedDarkMode'
import { formatDuration, useTodayTimeStats } from '@/hooks/useTodayTimeStats'
import { getErrorMessage } from '@/lib/api/error-utils'
import { memberRoleApi } from '@/lib/api/member-resources.api'
import { searchApi } from '@/lib/api/search'
import { cn } from '@/lib/utils'
import {
  getLinkWorkAllowedModuleTypes,
  LINK_WORK_SUPPORTED_TYPES,
} from '@/lib/utils/link-work-module-types'
import { isTauri } from '@/lib/utils/tauri'
import { getModuleLinkMeta } from '@/modules/module-link'
import { useOrganizationStore } from '@/store/organization.store'
import {
  SIDEBAR_THEMES,
  useUIPreferencesStore,
} from '@/store/ui-preferences.store'
import {
  CaretDownIcon,
  CaretRightIcon,
  CheckIcon,
  CircleNotchIcon,
  ClockIcon,
  InfoIcon,
  PauseIcon,
  PencilSimpleIcon,
  PlayIcon,
  SquareIcon,
  TrashIcon,
  UsersIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useQueries } from '@tanstack/react-query'
import { m } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { TimeEntry, TimeEntryLinkedModule } from '@scrambl/shared'
import {
  applySharedTaggedUserIds,
  countDistinctTaggedUserIds,
  getSharedTaggedUserIds,
  mergeLinkedModuleTags,
  ModuleTypeEnum,
  ResourceModuleSettingsSchema,
  setModuleTaggedUserIds,
  TimeEntryModuleSettingsSchema,
} from '@scrambl/shared'

import { useTimeEntry } from './TimeEntryProvider'
import { TimeEntryTagging } from './TimeEntryTagging'

type SessionEntry = TimeEntry & { elapsed?: number }

type ItemGroup = {
  key: string
  module: TimeEntryLinkedModule
  entries: SessionEntry[]
  totalSeconds: number
  isCurrent: boolean
}

type RemovalWarningState = {
  moduleName: string
  isCurrent: boolean
}

const LINKABLE_MODULE_TYPES = new Set<string>(LINK_WORK_SUPPORTED_TYPES)

const PRIMARY_ROLE_FALLBACK_WARNING =
  'No billing role selected. This entry will use your primary role for rate resolution.'

function formatClock(seconds: number) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

function getModuleKey(moduleType?: string | null, moduleId?: string | null) {
  if (!moduleType || !moduleId) {
    return null
  }
  return `${moduleType}:${moduleId}`
}

function getEntryDurationSeconds(entry: SessionEntry, activeEntryId?: string) {
  if (entry.id === activeEntryId && typeof entry.elapsed === 'number') {
    return entry.elapsed
  }

  if (!entry.startTime) {
    if (entry.duration !== null && entry.duration !== undefined) {
      return Math.max(0, Math.round(entry.duration * 3600))
    }
    return 0
  }

  const start = new Date(entry.startTime).getTime()
  const end = entry.endTime ? new Date(entry.endTime).getTime() : Date.now()
  const pausedSeconds = entry.pausedDuration || 0

  return Math.max(0, Math.floor((end - start - pausedSeconds * 1000) / 1000))
}

function formatEntryWindow(entry: SessionEntry) {
  if (!entry.startTime) {
    return 'No start time'
  }

  const formatter = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
  const start = formatter.format(new Date(entry.startTime))

  if (!entry.endTime) {
    return entry.isPaused ? `${start} - paused` : `${start} - now`
  }

  const end = formatter.format(new Date(entry.endTime))
  return `${start} - ${end}`
}

function getModuleContextText(moduleDetails?: ModuleSearchResult | null) {
  if (!moduleDetails) {
    return null
  }

  if (moduleDetails.type === 'PROJECT') {
    return moduleDetails.parentName ?? null
  }

  if (
    (moduleDetails.type === 'TASK' || moduleDetails.type === 'TASK_GROUP') &&
    moduleDetails.fullPath
  ) {
    const segments = moduleDetails.fullPath
      .split(' > ')
      .map(segment => segment.trim())
      .filter(Boolean)

    if (segments.length > 1) {
      return segments.slice(0, -1).join(' / ')
    }
  }

  return moduleDetails.parentName ?? null
}

function EntryDescriptionField({
  entry,
  fallbackDescription,
  onSave,
}: {
  entry: SessionEntry
  fallbackDescription?: string
  onSave: (description: string) => Promise<void>
}) {
  const [draft, setDraft] = useState(
    entry.description || fallbackDescription || '',
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const editorRef = useRef<HTMLDivElement>(null)
  const savedDescription = entry.description || fallbackDescription || ''

  useEffect(() => {
    setDraft(entry.description || fallbackDescription || '')
  }, [entry.description, fallbackDescription, entry.id])

  const handleSave = async () => {
    if (draft === savedDescription) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      await onSave(draft)
    } finally {
      setIsSaving(false)
      setIsEditing(false)
    }
  }

  const handleCancel = () => {
    setDraft(savedDescription)
    setIsEditing(false)
  }

  const preview =
    draft.trim() ||
    entry.description?.trim() ||
    fallbackDescription?.trim() ||
    ''

  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={() => setIsEditing(true)}
        className="group flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-muted/60"
      >
        <PencilSimpleIcon className="h-3 w-3 flex-shrink-0 text-muted-foreground/70 group-hover:text-muted-foreground" />
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-xs',
            preview
              ? 'text-foreground'
              : 'text-muted-foreground/70 group-hover:text-muted-foreground',
          )}
        >
          {preview || 'Add note'}
        </span>
        {isSaving ? (
          <CircleNotchIcon className="h-3 w-3 flex-shrink-0 animate-spin text-muted-foreground" />
        ) : null}
      </button>
    )
  }

  return (
    <div
      ref={editorRef}
      className="space-y-1.5"
      onBlur={event => {
        const nextTarget = event.relatedTarget
        if (
          nextTarget instanceof Node &&
          editorRef.current?.contains(nextTarget)
        ) {
          return
        }
        void handleSave()
      }}
    >
      <Textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            event.preventDefault()
            handleCancel()
          }
        }}
        rows={2}
        placeholder="Add a description for this log"
        autoFocus
        className="min-h-[64px] resize-none text-xs"
      />
      <div className="flex justify-end gap-1.5">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground"
          onMouseDown={event => event.preventDefault()}
          onClick={handleCancel}
          disabled={isSaving}
          title="Cancel editing"
        >
          <XIcon className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground"
          onMouseDown={event => event.preventDefault()}
          onClick={() => void handleSave()}
          disabled={isSaving}
          title="Save description"
        >
          {isSaving ? (
            <CircleNotchIcon className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <CheckIcon className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
    </div>
  )
}

function SessionDescriptionField({
  value,
  onDraftChange,
  onSave,
}: {
  value: string
  onDraftChange?: (description: string) => void
  onSave: (description: string) => void | Promise<void>
}) {
  const [draft, setDraft] = useState(value)
  const [isSaving, setIsSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const editorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setDraft(value)
  }, [value])

  const handleDraftChange = (description: string) => {
    setDraft(description)
    onDraftChange?.(description)
  }

  const handleSave = async () => {
    if (draft === value) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      await onSave(draft)
    } finally {
      setIsSaving(false)
      setIsEditing(false)
    }
  }

  const handleCancel = () => {
    setDraft(value)
    onDraftChange?.(value)
    setIsEditing(false)
  }

  const preview = draft.trim() || value.trim()

  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={() => setIsEditing(true)}
        className="flex w-full items-center gap-2 rounded-lg border border-border/60 bg-background/60 px-2.5 py-2 text-left transition-colors hover:border-border hover:bg-background"
      >
        <PencilSimpleIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-sm',
            preview ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {preview || 'Add a description for this entry'}
        </span>
        {isSaving ? (
          <CircleNotchIcon className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-muted-foreground" />
        ) : null}
      </button>
    )
  }

  return (
    <div
      ref={editorRef}
      className="space-y-1.5"
      onBlur={event => {
        const nextTarget = event.relatedTarget
        if (
          nextTarget instanceof Node &&
          editorRef.current?.contains(nextTarget)
        ) {
          return
        }
        void handleSave()
      }}
    >
      <Textarea
        value={draft}
        onChange={e => handleDraftChange(e.target.value)}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            event.preventDefault()
            handleCancel()
          }
        }}
        rows={2}
        placeholder="Add a description for this entry"
        autoFocus
        className="min-h-[72px] resize-none text-sm"
      />
      <div className="flex justify-end gap-1.5">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground"
          onMouseDown={event => event.preventDefault()}
          onClick={handleCancel}
          disabled={isSaving}
          title="Cancel editing"
        >
          <XIcon className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground"
          onMouseDown={event => event.preventDefault()}
          onClick={() => void handleSave()}
          disabled={isSaving}
          title="Save description"
        >
          {isSaving ? (
            <CircleNotchIcon className="h-4 w-4 animate-spin" />
          ) : (
            <CheckIcon className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  )
}

function InfoHint({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={450} skipDelayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="More information"
          >
            <InfoIcon className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[220px]">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function TimeEntryWidget() {
  const isExternalUser = useIsExternalUser()
  const { isPersonal } = useAccountType()

  // Personal ("My Space") accounts have TIME_TRACKING off — no timer chip.
  // Gate on the account type (not the flag) so business orgs are never affected
  // regardless of their global TIME_TRACKING default.
  if (isExternalUser || isPersonal) {
    return null
  }

  return <TimeEntryWidgetContent />
}

function TimeEntryWidgetContent() {
  const {
    activeEntry,
    isTracking,
    isPaused,
    startTimer,
    stopTimer,
    pauseTimer,
    resumeTimer,
    switchTimerItem,
    discardTimer,
    updateActiveEntry,
  } = useTimeEntry()

  const updateTimeEntryMutation = useUpdateTimeEntryMutation()
  const { data: settingsData } = useOrganizationSettingsQuery()
  const timeSettings = TimeEntryModuleSettingsSchema.parse(
    settingsData?.effectiveSettings?.modules?.TIME_ENTRY || {},
  )
  const teamSettings = ResourceModuleSettingsSchema.parse(
    settingsData?.effectiveSettings?.modules?.TEAM || {},
  )
  const showBillingConfigurationWarnings =
    teamSettings.billingConfiguration.enforceBillingConfiguration
  const linkWorkAllowedModuleTypes = (() => {
    return getLinkWorkAllowedModuleTypes(timeSettings.allowedBillToTypes)
  })()

  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [expandedItemKeys, setExpandedItemKeys] = useState<Set<string>>(
    new Set(),
  )
  const [isStarting, setIsStarting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isStopping, setIsStopping] = useState(false)
  const [switchingItemKey, setSwitchingItemKey] = useState<string | null>(null)
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  )
  const [removalWarning, setRemovalWarning] =
    useState<RemovalWarningState | null>(null)
  const [isDiscardWarningOpen, setIsDiscardWarningOpen] = useState(false)
  const [sessionDescription, setSessionDescription] = useState('')
  const sessionDescriptionSeedRef = useRef<string | null>(null)
  const [pendingSplitEntry, setPendingSplitEntry] = useState<TimeEntry | null>(
    null,
  )
  const [pendingSplitExistingEntries, setPendingSplitExistingEntries] =
    useState<TimeEntry[]>([])
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false)
  const [isSplitRequired, setIsSplitRequired] = useState(false)
  const [splitTotalMinutes, setSplitTotalMinutes] = useState<number | null>(
    null,
  )
  const [splitEndTime, setSplitEndTime] = useState<Date | null>(null)
  const [splitDescription, setSplitDescription] = useState('')
  const [resumeAfterSplitCancel, setResumeAfterSplitCancel] = useState(false)

  const isMobile = useIsMobile()
  const { theme } = useTheme()
  const sidebarTheme = useUIPreferencesStore(state => state.sidebarTheme)
  const isDesktopApp = isTauri() && !isMobile
  const isDarkMode = useResolvedDarkMode(theme)
  const themeConfig =
    SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0]
  const useDarkText = isDesktopApp
    ? !isDarkMode && themeConfig.lightTextColor === 'dark'
    : !isDarkMode

  const { data: timerSessionEntries = [] } = useTimerSessionEntriesQuery(
    activeEntry?.timerSessionId,
    {
      enabled: !!activeEntry?.timerSessionId,
    },
  )

  const resolvedLinkedModules = (() => {
    const modules = new Map<string, TimeEntryLinkedModule>()

    // Keep the order items were added — never hoist the active primary to the
    // top. This list is also persisted back on switch, so hoisting here would
    // permanently reshuffle the session.
    for (const module of activeEntry?.linkedModules || []) {
      modules.set(`${module.moduleType}:${module.moduleId}`, module)
    }

    const currentPrimaryKey = getModuleKey(
      activeEntry?.moduleType,
      activeEntry?.moduleId,
    )

    if (
      currentPrimaryKey &&
      activeEntry?.moduleType &&
      activeEntry?.moduleId &&
      !modules.has(currentPrimaryKey)
    ) {
      modules.set(currentPrimaryKey, {
        moduleType: activeEntry.moduleType as ModuleTypeEnum,
        moduleId: activeEntry.moduleId,
        moduleName: activeEntry.moduleName ?? null,
      })
    }

    return Array.from(modules.values())
  })()

  const taggedMembersCount = countDistinctTaggedUserIds(resolvedLinkedModules)
  const sharedTaggedUserIds = getSharedTaggedUserIds(resolvedLinkedModules)
  const linkedModuleByKey = new Map(
    resolvedLinkedModules.map(module => [
      `${module.moduleType}:${module.moduleId}`,
      module,
    ]),
  )

  const sessionEntries = (() => {
    if (!activeEntry) {
      return []
    }

    const byId = new Map<string, SessionEntry>()

    for (const entry of timerSessionEntries) {
      byId.set(entry.id, entry)
    }

    byId.set(activeEntry.id, activeEntry)

    return Array.from(byId.values()).sort((left, right) => {
      const leftTime = left.startTime ? new Date(left.startTime).getTime() : 0
      const rightTime = right.startTime
        ? new Date(right.startTime).getTime()
        : 0
      return leftTime - rightTime
    })
  })()

  const sessionDescriptionSeedKey = activeEntry?.id ?? null

  useEffect(() => {
    if (sessionDescriptionSeedRef.current === sessionDescriptionSeedKey) {
      return
    }

    sessionDescriptionSeedRef.current = sessionDescriptionSeedKey

    const seedDescription = activeEntry?.description?.trim() || ''

    setSessionDescription(seedDescription)
  }, [activeEntry?.description, sessionDescriptionSeedKey])

  const currentItemKey = getModuleKey(
    activeEntry?.moduleType,
    activeEntry?.moduleId,
  )

  const itemGroups = (() => {
    const order: string[] = []
    const groups = new Map<string, ItemGroup>()

    const ensureGroup = (module: TimeEntryLinkedModule) => {
      const key = `${module.moduleType}:${module.moduleId}`
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          module,
          entries: [],
          totalSeconds: 0,
          isCurrent: key === currentItemKey,
        })
        order.push(key)
      }
      return groups.get(key)!
    }

    for (const module of resolvedLinkedModules) {
      ensureGroup(module)
    }

    for (const entry of sessionEntries) {
      if (!entry.moduleType || !entry.moduleId) {
        continue
      }

      const group = ensureGroup({
        moduleType: entry.moduleType as ModuleTypeEnum,
        moduleId: entry.moduleId,
        moduleName: entry.moduleName ?? null,
      })
      group.entries.push(entry)
      group.totalSeconds += getEntryDurationSeconds(entry, activeEntry?.id)
      if (!group.module.moduleName && entry.moduleName) {
        group.module.moduleName = entry.moduleName
      }
    }

    return order
      .map(key => groups.get(key))
      .filter((group): group is ItemGroup => Boolean(group))
      .map(group => ({
        ...group,
        isCurrent: group.key === currentItemKey,
      }))
  })()

  const linkedModuleQueries = useQueries({
    queries: itemGroups.map(group => {
      const moduleType = String(group.module.moduleType).toUpperCase()

      return {
        queryKey: [
          'time-entry-linked-module',
          moduleType,
          group.module.moduleId,
        ],
        queryFn: () =>
          searchApi.getModuleById(moduleType, group.module.moduleId),
        enabled: LINKABLE_MODULE_TYPES.has(moduleType),
        staleTime: 5 * 60 * 1000,
      }
    }),
  })

  const linkedModuleDetailsByKey = (() => {
    return new Map(
      itemGroups.map((group, index) => [
        group.key,
        linkedModuleQueries[index]?.data ?? null,
      ]),
    )
  })()

  // Resolve the (projectId, entityId) scope we should use to look up which
  // roles the current member is eligible to log time under for each item.
  // Follows the same precedence as the rate cascade: prefer project scope,
  // fall back to entity scope.
  const moduleScopeByKey = (() => {
    const map = new Map<
      string,
      { projectId: string | null; entityId: string | null }
    >()
    for (const group of itemGroups) {
      const details = linkedModuleDetailsByKey.get(group.key)
      const moduleType = String(group.module.moduleType).toUpperCase()
      let projectId: string | null = null
      let entityId: string | null = null
      if (moduleType === 'PROJECT') {
        projectId = group.module.moduleId
        entityId = details?.entityId ?? null
      } else if (moduleType === 'ENTITY') {
        entityId = group.module.moduleId
      } else if (moduleType === 'TASK' || moduleType === 'TASK_GROUP') {
        projectId = details?.projectId ?? null
        entityId = details?.entityId ?? null
      }
      map.set(group.key, { projectId, entityId })
    }
    return map
  })()

  const { getCurrentUserMembership } = useOrganizationStore()
  const currentMembership = getCurrentUserMembership()
  const currentMemberId = currentMembership?.id ?? null

  // Today totals for the summary strip — mirrors the dashboard Time Tracker.
  // Only fetched while the details popover is open.
  const navigate = useNavigate()
  const orgSlug = useOrganizationStore(state => state.currentOrganization?.slug)
  const { stats: todayStats } = useTodayTimeStats({ enabled: isDetailsOpen })

  const handleOpenMyTime = () => {
    if (!orgSlug) return
    setIsDetailsOpen(false)
    navigate(`/${orgSlug}/my-time`)
  }

  const availableRolesQueries = useQueries({
    queries: itemGroups.map(group => {
      const scope = moduleScopeByKey.get(group.key) ?? {
        projectId: null,
        entityId: null,
      }
      const hasScope = Boolean(scope.projectId || scope.entityId)
      return {
        queryKey: [
          'time-entry-available-roles',
          currentMemberId,
          scope.projectId,
          scope.entityId,
        ],
        queryFn: () =>
          memberRoleApi.getAvailable({
            memberId: currentMemberId!,
            projectId: scope.projectId,
            entityId: scope.projectId ? null : scope.entityId,
            requireRate: true,
          }),
        enabled: Boolean(currentMemberId) && hasScope,
        staleTime: 5 * 60 * 1000,
      }
    }),
  })

  const availableRolesByKey = (() => {
    return new Map(
      itemGroups.map((group, index) => [
        group.key,
        availableRolesQueries[index]?.data ?? [],
      ]),
    )
  })()

  // Pending role selection per non-current item, applied when the user
  // switches to that item. Keyed by `${moduleType}:${moduleId}`.
  const [pendingRoleByKey, setPendingRoleByKey] = useState<
    Record<string, string | undefined>
  >({})

  // Auto-assign the role on the active entry when exactly one role is eligible
  // for the currently-tracked item and no role has been set yet. This mirrors
  // the Log Time tab's auto-resolve behavior so timer-created entries don't
  // arrive on the week view as "Select role".
  const autoAssignedRoleKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!activeEntry) return
    if (activeEntry.roleId) return
    const currentKey = getModuleKey(
      activeEntry.moduleType,
      activeEntry.moduleId,
    )
    if (!currentKey) return
    const roles = availableRolesByKey.get(currentKey) ?? []
    if (roles.length !== 1) return
    const soleRoleId = roles[0]?.roleId
    if (!soleRoleId) return
    // Guard against repeatedly firing for the same (entry, item) pair.
    const guardKey = `${activeEntry.id}:${currentKey}`
    if (autoAssignedRoleKeyRef.current === guardKey) return
    autoAssignedRoleKeyRef.current = guardKey
    void updateActiveEntry({ roleId: soleRoleId }).catch(() => {
      autoAssignedRoleKeyRef.current = null
    })
  }, [activeEntry, availableRolesByKey, updateActiveEntry])

  const sessionTotalSeconds = (() => {
    return sessionEntries.reduce((sum, entry) => {
      return sum + getEntryDurationSeconds(entry, activeEntry?.id)
    }, 0)
  })()

  const pendingSplitModules = (() => {
    return itemGroups
      .filter(group => {
        return (
          group.isCurrent ||
          (group.entries.length === 0 && group.totalSeconds === 0)
        )
      })
      .map(group => group.module)
  })()

  const shouldReviewSessionBeforeStop =
    itemGroups.length > 1 || sessionEntries.length > 1

  const setItemExpanded = (key: string, open: boolean) => {
    setExpandedItemKeys(prev => {
      const next = new Set(prev)
      if (open) {
        next.add(key)
      } else {
        next.delete(key)
      }
      return next
    })
  }

  const validateBeforeFinalize = (action: 'stop' | 'switch') => {
    const hasDescription = Boolean(
      activeEntry?.description?.trim() || sessionDescription.trim(),
    )
    const hasModuleLink = Boolean(
      activeEntry?.moduleType && activeEntry?.moduleId,
    )

    if (timeSettings.requireDescription && !hasDescription) {
      setValidationMessage(
        action === 'switch'
          ? 'Add a description to the active item before switching.'
          : 'Add a description to the active item before ending the timer.',
      )
      setIsDetailsOpen(true)
      if (currentItemKey) {
        setItemExpanded(currentItemKey, true)
      }
      return false
    }

    if (timeSettings.requireModuleLink && !hasModuleLink) {
      setValidationMessage(
        'Link the active timer to an item before continuing.',
      )
      setIsDetailsOpen(true)
      return false
    }

    setValidationMessage(null)
    return true
  }

  const handleLinkedModulesChange = async (
    pickedModules: TimeEntryLinkedModule[],
  ) => {
    if (!activeEntry) {
      return
    }

    // The picker rebuilds module objects; keep each item's tag list.
    const nextModules = mergeLinkedModuleTags(
      resolvedLinkedModules,
      pickedModules,
    )

    const currentPrimaryKey = getModuleKey(
      activeEntry.moduleType,
      activeEntry.moduleId,
    )
    const containsCurrentPrimary = currentPrimaryKey
      ? nextModules.some(
          module =>
            `${module.moduleType}:${module.moduleId}` === currentPrimaryKey,
        )
      : false

    if (currentPrimaryKey && !containsCurrentPrimary) {
      toast.error(
        'Switch to another linked item before removing the active one.',
      )
      return
    }

    if (nextModules.length === 0) {
      await updateActiveEntry({
        linkedModules: [],
        moduleType: null,
        moduleId: null,
        moduleName: null,
      })
      return
    }

    const isSelectingFirstItem =
      !currentPrimaryKey &&
      Boolean(activeEntry.entryType === 'TIMER' && activeEntry.startTime)

    const nextPrimary =
      (currentPrimaryKey
        ? nextModules.find(
            module =>
              `${module.moduleType}:${module.moduleId}` === currentPrimaryKey,
          )
        : undefined) ?? nextModules[0]

    await updateActiveEntry({
      linkedModules: nextModules,
      moduleType: nextPrimary?.moduleType ?? null,
      moduleId: nextPrimary?.moduleId ?? null,
      moduleName: nextPrimary?.moduleName ?? null,
    })

    if (isSelectingFirstItem) {
      setValidationMessage(null)
    }
  }

  const handleSharedTagsChange = async (userIds: string[]) => {
    if (!activeEntry) return
    await updateActiveEntry({
      linkedModules: applySharedTaggedUserIds(resolvedLinkedModules, userIds),
    })
  }

  const handleItemTagsChange = async (itemKey: string, userIds: string[]) => {
    if (!activeEntry) return
    await updateActiveEntry({
      linkedModules: setModuleTaggedUserIds(
        resolvedLinkedModules,
        itemKey,
        userIds,
      ),
    })
  }

  const handleStart = async () => {
    setIsStarting(true)
    try {
      const firstLinkedItem = resolvedLinkedModules[0]
      const primaryModuleType =
        firstLinkedItem?.moduleType === 'TASK_GROUP'
          ? undefined
          : firstLinkedItem?.moduleType
      await startTimer({
        moduleType: primaryModuleType,
        moduleId: primaryModuleType ? firstLinkedItem?.moduleId : undefined,
        linkedModules:
          resolvedLinkedModules.length > 0 ? resolvedLinkedModules : undefined,
      })
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to start timer'))
    } finally {
      setIsStarting(false)
    }
  }

  const handleStop = async () => {
    if (!activeEntry) {
      return
    }

    setIsStopping(true)
    try {
      const descriptionFallback = sessionDescription.trim()
      const activeDescription =
        activeEntry.description?.trim() || descriptionFallback || undefined

      if (shouldReviewSessionBeforeStop && pendingSplitModules.length > 0) {
        const elapsedSeconds = activeEntry.elapsed || 0
        const roundedMinutes = Math.round(elapsedSeconds / 60)
        setSplitTotalMinutes(
          Math.max(elapsedSeconds > 0 ? 1 : 0, roundedMinutes),
        )

        let endTime = activeEntry.pausedAt
          ? new Date(activeEntry.pausedAt)
          : new Date()
        let shouldResume = false

        if (!isPaused) {
          const pausedEntry = await pauseTimer()
          if (pausedEntry) {
            shouldResume = true
            if (pausedEntry.pausedAt) {
              endTime = new Date(pausedEntry.pausedAt)
            }
          }
        }

        setSplitEndTime(endTime)
        setSplitDescription(activeDescription ?? '')
        setResumeAfterSplitCancel(shouldResume)
        setPendingSplitEntry({
          ...activeEntry,
          description: activeDescription || activeEntry.description,
          linkedModules: pendingSplitModules,
        })
        setPendingSplitExistingEntries(
          sessionEntries.filter(entry => entry.id !== activeEntry.id),
        )
        setIsSplitDialogOpen(true)
        setIsSplitRequired(true)
        setIsDetailsOpen(false)
        setValidationMessage(null)
        return
      }

      if (!validateBeforeFinalize('stop')) {
        return
      }

      const entry = await stopTimer({
        description: activeDescription,
      })
      if (entry) {
        setIsDetailsOpen(false)
        setValidationMessage(null)
        toast.success('Time entry saved')
      } else {
        toast.error('Failed to save time entry')
      }
    } finally {
      setIsStopping(false)
    }
  }

  const handleDiscard = async () => {
    if (sessionEntries.length > 1 && !isDiscardWarningOpen) {
      setIsDiscardWarningOpen(true)
      return
    }

    setIsDeleting(true)
    try {
      await discardTimer()
      setIsDetailsOpen(false)
      setValidationMessage(null)
      setIsDiscardWarningOpen(false)
    } finally {
      setIsDeleting(false)
    }
  }

  const resetSplitState = () => {
    setIsSplitDialogOpen(false)
    setPendingSplitEntry(null)
    setPendingSplitExistingEntries([])
    setIsSplitRequired(false)
    setSplitTotalMinutes(null)
    setSplitEndTime(null)
    setSplitDescription('')
    setResumeAfterSplitCancel(false)
  }

  const handleSplitCancel = async () => {
    if (resumeAfterSplitCancel) {
      await resumeTimer()
    }
    resetSplitState()
  }

  const handleSwitchToItem = async (module: TimeEntryLinkedModule) => {
    if (!activeEntry) {
      return
    }

    if (!validateBeforeFinalize('switch')) {
      return
    }

    const itemKey = `${module.moduleType}:${module.moduleId}`
    setSwitchingItemKey(itemKey)
    try {
      const descriptionToPersist =
        activeEntry.description?.trim() || sessionDescription.trim()

      if (descriptionToPersist) {
        await updateTimeEntryMutation.mutateAsync({
          id: activeEntry.id,
          data: { description: descriptionToPersist },
        })
      }

      const pendingRoleId = pendingRoleByKey[itemKey]
      const switchedEntry = await switchTimerItem(module, {
        linkedModules: resolvedLinkedModules,
        ...(pendingRoleId ? { roleId: pendingRoleId } : {}),
      })
      if (switchedEntry && pendingRoleId) {
        setPendingRoleByKey(prev => {
          const next = { ...prev }
          delete next[itemKey]
          return next
        })
      }
      if (!switchedEntry) {
        toast.error('Failed to switch timer item')
      } else {
        setValidationMessage(null)
      }
    } finally {
      setSwitchingItemKey(null)
    }
  }

  const handleSaveSessionDescription = async (description: string) => {
    setSessionDescription(description)
    await updateActiveEntry({ description })
  }

  const handleSaveEntryDescription = async (
    entry: SessionEntry,
    description: string,
  ) => {
    if (activeEntry?.id === entry.id) {
      await updateActiveEntry({ description })
      return
    }

    await updateTimeEntryMutation.mutateAsync({
      id: entry.id,
      data: { description },
    })
  }

  const handleRemoveLinkedItem = async (group: ItemGroup) => {
    const hasTrackedTime = group.entries.length > 0 || group.totalSeconds > 0

    if (hasTrackedTime) {
      setRemovalWarning({
        moduleName: group.module.moduleName || 'This item',
        isCurrent: group.isCurrent,
      })
      return
    }

    await handleLinkedModulesChange(
      resolvedLinkedModules.filter(module => {
        return (
          module.moduleType !== group.module.moduleType ||
          module.moduleId !== group.module.moduleId
        )
      }),
    )
  }

  const containerClasses = cn(
    'flex items-center gap-2 rounded-full px-3 py-1.5 shadow-sm backdrop-blur-sm',
    useDarkText
      ? 'border border-black/20 bg-black/10'
      : 'border border-white/20 bg-white/10',
    isTracking &&
      (useDarkText ? 'border-black/30 shadow-md' : 'border-white/30 shadow-md'),
  )
  const textClasses = useDarkText ? 'text-black' : 'text-white'
  const textMutedClasses = useDarkText ? 'text-black/70' : 'text-white/70'
  const bgMutedClasses = useDarkText ? 'bg-black/10' : 'bg-white/10'

  // Keep the split dialog mounted after beforeSplit stops the active timer.
  // Its mutation-level success callback only runs while the dialog remains
  // subscribed; unmounting here leaves the open state behind for the next
  // timer session.
  const splitDialog = (
    <SplitTimeEntryDialog
      open={isSplitDialogOpen}
      entry={pendingSplitEntry}
      linkedModulesOverride={pendingSplitEntry?.linkedModules}
      existingEntries={pendingSplitExistingEntries}
      allowedModuleTypes={linkWorkAllowedModuleTypes}
      requireCompletion={isSplitRequired}
      allowCancel
      totalMinutesOverride={splitTotalMinutes ?? undefined}
      pendingDescription={splitDescription}
      pendingDescriptionRequired={timeSettings.requireDescription}
      onPendingDescriptionChange={setSplitDescription}
      beforeSplit={async () => {
        const activeDescription =
          splitDescription.trim() ||
          activeEntry?.description?.trim() ||
          sessionDescription.trim() ||
          undefined
        const endTime = splitEndTime ?? new Date()
        return stopTimer({
          endTime,
          description: activeDescription,
        })
      }}
      onCancel={handleSplitCancel}
      onOpenChange={setIsSplitDialogOpen}
      onSplitComplete={() => {
        resetSplitState()
        setValidationMessage(null)
        toast.success('Time entries created')
      }}
    />
  )

  if (!timeSettings.allowTimerEntry && !activeEntry) {
    return null
  }

  if (isStarting) {
    return (
      <>
        <div
          className={cn(
            'flex animate-pulse items-center gap-2 rounded-full border px-4 py-2 shadow-md backdrop-blur-sm',
            useDarkText
              ? 'border-black/30 bg-black/20'
              : 'border-white/30 bg-white/20',
          )}
        >
          <div className="relative">
            <div
              className={cn(
                'h-3 w-3 animate-ping rounded-full',
                useDarkText ? 'bg-black' : 'bg-white',
              )}
            />
            <div
              className={cn(
                'absolute inset-0 h-3 w-3 rounded-full',
                useDarkText ? 'bg-black/60' : 'bg-white/60',
              )}
            />
          </div>
          <CircleNotchIcon
            className={cn('h-4 w-4 animate-spin', textClasses)}
          />
          <span className={cn('text-sm font-medium', textClasses)}>
            Starting timer...
          </span>
        </div>
        {splitDialog}
      </>
    )
  }

  if (!activeEntry) {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleStart}
          className={cn(
            'transition-tonal h-8 border px-3 text-sm duration-200 hover:scale-105 hover:shadow-md',
            useDarkText
              ? 'border-black/30 bg-black/5 text-black hover:bg-black/10'
              : 'border-white/30 bg-white/5 text-white hover:bg-white/10',
          )}
          data-start-timer
          disabled={isStarting}
        >
          <ClockIcon className="mr-1.5 h-4 w-4" />
          <span>Timer</span>
        </Button>
        {splitDialog}
      </>
    )
  }

  return (
    <>
      {/* The session pill settles in when a timer starts (transform +
          opacity only). data-timer-active lets the floating title bar fit
          it to its island. */}
      <m.div
        data-timer-active
        className={containerClasses}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
      >
        <ConfirmDialog
          open={!!removalWarning}
          onOpenChange={open => {
            if (!open) {
              setRemovalWarning(null)
            }
          }}
          title="Time is already attached"
          description={
            removalWarning?.isCurrent
              ? `${removalWarning.moduleName} is the current tracked item and already has time logged in this session. Switch away from it or remove the time logs first.`
              : `${removalWarning?.moduleName} already has time logged in this session, so it needs to stay in the list. Remove the time logs first if you want to clear it completely.`
          }
          cancelText="OK"
          onConfirm={() => {}}
          showConfirmButton={false}
          icon={<WarningCircleIcon className="h-5 w-5 text-amber-600" />}
        />
        <ConfirmDialog
          open={isDiscardWarningOpen}
          onOpenChange={setIsDiscardWarningOpen}
          title="Delete this timer session?"
          description={`This will delete all ${sessionEntries.length} logs created in this session, not just the current item.`}
          confirmText="Delete session"
          cancelText="Keep session"
          onConfirm={() => void handleDiscard()}
          icon={<WarningCircleIcon className="h-5 w-5 text-destructive" />}
        />

        <div className="flex items-center gap-2">
          <div
            className={cn(
              'transition-tonal h-2 w-2 rounded-full duration-200',
              isTracking ? 'bg-emerald-500' : 'bg-amber-500',
            )}
          />
          <span
            className={cn(
              'font-mono text-sm font-medium tabular-nums',
              textClasses,
            )}
          >
            {formatClock(sessionTotalSeconds)}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {itemGroups.length > 0 && (
            <div
              className={cn(
                'flex items-center gap-1 rounded-full px-2 py-0.5 text-xs',
                textMutedClasses,
                bgMutedClasses,
              )}
            >
              <span className="max-w-[90px] truncate">
                {itemGroups.find(group => group.isCurrent)?.module.moduleName ||
                  'Linked work'}
              </span>
              {itemGroups.length > 1 ? (
                <span>+{itemGroups.length - 1}</span>
              ) : null}
            </div>
          )}

          {taggedMembersCount > 0 && (
            <div
              className={cn(
                'flex items-center gap-1 rounded-full px-2 py-0.5 text-xs',
                textMutedClasses,
                bgMutedClasses,
              )}
            >
              <UsersIcon className="h-3 w-3" />
              <span>{taggedMembersCount}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          {isTracking ? (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-7 w-7',
                useDarkText
                  ? 'text-black/70 hover:bg-black/10 hover:text-black'
                  : 'text-white/70 hover:bg-white/10 hover:text-white',
              )}
              onClick={() => void pauseTimer()}
              title="Pause Timer"
            >
              <PauseIcon className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-7 w-7',
                useDarkText
                  ? 'text-black/70 hover:bg-black/10 hover:text-black'
                  : 'text-white/70 hover:bg-white/10 hover:text-white',
              )}
              onClick={() => void resumeTimer()}
              title="Resume Timer"
            >
              <PlayIcon className="h-3.5 w-3.5" />
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-7 w-7',
              useDarkText
                ? 'text-black/70 hover:bg-emerald-500/20 hover:text-emerald-700'
                : 'text-white/70 hover:bg-emerald-500/20 hover:text-emerald-400',
            )}
            onClick={handleStop}
            title="Stop and Save"
          >
            <SquareIcon className="h-3.5 w-3.5" />
          </Button>

          <Popover open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'h-7 w-7',
                  useDarkText
                    ? 'text-black/70 hover:bg-black/10 hover:text-black'
                    : 'text-white/70 hover:bg-white/10 hover:text-white',
                )}
                title="Manage Linked Items"
              >
                <CaretDownIcon className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="flex max-h-[min(85vh,46rem)] w-[min(30rem,calc(100vw-2rem))] flex-col overflow-hidden p-0"
              align="end"
              onOpenAutoFocus={event => {
                event.preventDefault()
              }}
              // The Link Work results panel portals to document.body so it
              // can escape this popover's overflow-hidden — without these
              // guards, interacting with it would dismiss the popover.
              onInteractOutside={event => {
                const target = event.detail.originalEvent.target
                if (
                  target instanceof HTMLElement &&
                  target.closest('[data-link-work-dropdown]')
                ) {
                  event.preventDefault()
                }
              }}
              onEscapeKeyDown={event => {
                if (document.querySelector('[data-link-work-dropdown]')) {
                  event.preventDefault()
                }
              }}
            >
              {/* Session header — the dashboard Time Tracker card, condensed */}
              <div className="shrink-0 border-b bg-muted/20 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div
                      className={cn(
                        'flex items-center gap-1.5 text-xs font-medium',
                        isPaused
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-emerald-600 dark:text-emerald-400',
                      )}
                    >
                      <span
                        className={cn(
                          'h-1.5 w-1.5 rounded-full',
                          isPaused
                            ? 'bg-amber-500'
                            : 'animate-pulse bg-emerald-500',
                        )}
                      />
                      {isPaused ? 'Paused' : 'Tracking'}
                    </div>
                    <div className="font-mono text-2xl font-semibold tabular-nums leading-tight tracking-tight">
                      {formatClock(sessionTotalSeconds)}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {itemGroups.find(group => group.isCurrent)?.module
                        .moduleName ||
                        sessionDescription.trim() ||
                        'No work linked yet'}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {isTracking ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5 rounded-full px-3"
                        onClick={() => void pauseTimer()}
                      >
                        <PauseIcon className="h-3.5 w-3.5" />
                        Pause
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5 rounded-full px-3"
                        onClick={() => void resumeTimer()}
                      >
                        <PlayIcon className="h-3.5 w-3.5" />
                        Resume
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {validationMessage ? (
                <div className="shrink-0 px-4 pt-3">
                  <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">
                    <WarningCircleIcon className="h-5 w-5 flex-shrink-0" />
                    <span>{validationMessage}</span>
                  </div>
                </div>
              ) : null}

              <div className="flex min-h-0 flex-1 flex-col px-4 py-3">
                <div className="shrink-0 pb-3">
                  <LinkedModulePicker
                    className="w-full max-w-full"
                    value={resolvedLinkedModules}
                    onChange={handleLinkedModulesChange}
                    allowedModuleTypes={linkWorkAllowedModuleTypes}
                    mode="picker"
                    helperText="Search to add items · press play on a card to switch the timer."
                  />
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto pr-2">
                  <div className="space-y-2">
                    {itemGroups.length === 0 ? (
                      <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
                        Add an item to begin.
                      </div>
                    ) : (
                      itemGroups.map(group => {
                        const isSwitching = switchingItemKey === group.key
                        const hasExpandableLogs = group.entries.length > 0
                        const canRemoveInline =
                          group.entries.length === 0 && group.totalSeconds === 0
                        const typeMeta = getModuleLinkMeta(
                          group.module.moduleType,
                        )
                        const TypeIcon = typeMeta.icon
                        const moduleContext = getModuleContextText(
                          linkedModuleDetailsByKey.get(group.key),
                        )

                        return (
                          <Collapsible
                            key={group.key}
                            open={expandedItemKeys.has(group.key)}
                            onOpenChange={open =>
                              setItemExpanded(group.key, open)
                            }
                          >
                            <div
                              className={cn(
                                'rounded-xl border p-3',
                                group.isCurrent
                                  ? 'border-primary/40 bg-primary/5'
                                  : 'border-border/60 bg-background',
                              )}
                            >
                              <div className="flex items-center justify-between gap-2.5">
                                <div className="flex min-w-0 flex-1 items-center gap-2.5">
                                  <div
                                    className={cn(
                                      'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
                                      typeMeta.bgClass,
                                    )}
                                  >
                                    <TypeIcon
                                      className={cn(
                                        'h-4 w-4',
                                        typeMeta.textClass,
                                      )}
                                    />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <div className="truncate text-sm font-medium">
                                        {group.module.moduleName ||
                                          'Linked item'}
                                      </div>
                                      {group.isCurrent ? (
                                        <Badge
                                          variant="outline"
                                          className="h-5 shrink-0 px-1.5 text-[10px] uppercase tracking-wide"
                                        >
                                          {isPaused ? 'Paused' : 'Active'}
                                        </Badge>
                                      ) : null}
                                    </div>
                                    {(() => {
                                      const metaParts = [
                                        group.entries.length > 1
                                          ? `${group.entries.length} logs`
                                          : null,
                                        moduleContext,
                                      ].filter(Boolean)
                                      return metaParts.length > 0 ? (
                                        <div className="mt-0.5 truncate text-xs text-muted-foreground">
                                          {metaParts.join(' · ')}
                                        </div>
                                      ) : null
                                    })()}
                                  </div>
                                </div>

                                <div className="flex items-center gap-1.5">
                                  <div className="min-w-[72px] text-right font-mono text-xs tabular-nums text-muted-foreground">
                                    {formatClock(group.totalSeconds)}
                                  </div>
                                  {group.isCurrent ? (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() => {
                                        if (isTracking) {
                                          void pauseTimer()
                                        } else {
                                          void resumeTimer()
                                        }
                                      }}
                                      title={
                                        isTracking
                                          ? 'Pause current item'
                                          : 'Resume current item'
                                      }
                                    >
                                      {isTracking ? (
                                        <PauseIcon className="h-4 w-4" />
                                      ) : (
                                        <PlayIcon className="h-4 w-4" />
                                      )}
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() =>
                                        void handleSwitchToItem(group.module)
                                      }
                                      disabled={isSwitching || isStopping}
                                      title="Switch to this item"
                                    >
                                      {isSwitching ? (
                                        <CircleNotchIcon className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <PlayIcon className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}

                                  <CollapsibleTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      title="Show details"
                                    >
                                      <CaretRightIcon className="h-4 w-4 transition-transform data-[state=open]:rotate-90" />
                                    </Button>
                                  </CollapsibleTrigger>
                                </div>
                              </div>

                              <CollapsibleContent className="pt-3">
                                <div className="space-y-3 border-t pt-3">
                                  {group.isCurrent ? (
                                    <SessionDescriptionField
                                      value={sessionDescription}
                                      onDraftChange={setSessionDescription}
                                      onSave={handleSaveSessionDescription}
                                    />
                                  ) : null}

                                  {(() => {
                                    const roles =
                                      availableRolesByKey.get(group.key) ?? []
                                    if (roles.length === 0) {
                                      return null
                                    }
                                    const primaryRole =
                                      roles.find(role =>
                                        Boolean(role.isPrimary),
                                      ) ?? roles[0]
                                    if (roles.length === 1) {
                                      return (
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                          <span className="font-medium">
                                            Billing role
                                          </span>
                                          <span>
                                            {primaryRole?.role?.name ??
                                              'Unnamed role'}
                                          </span>
                                        </div>
                                      )
                                    }
                                    const selectedRoleId = group.isCurrent
                                      ? (activeEntry?.roleId ?? undefined)
                                      : pendingRoleByKey[group.key]
                                    return (
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-medium text-muted-foreground">
                                            Billing role
                                          </span>
                                          {showBillingConfigurationWarnings &&
                                          !selectedRoleId ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                                              <WarningCircleIcon
                                                className="size-3.5"
                                                weight="fill"
                                              />
                                              <span>
                                                {primaryRole?.role?.name
                                                  ? `Primary role fallback: ${primaryRole.role.name}`
                                                  : 'Primary role fallback'}
                                              </span>
                                            </span>
                                          ) : null}
                                        </div>
                                        <Select
                                          value={selectedRoleId ?? 'none'}
                                          onValueChange={async value => {
                                            const nextRoleId =
                                              value === 'none' ? null : value
                                            if (group.isCurrent) {
                                              try {
                                                await updateActiveEntry({
                                                  roleId: nextRoleId,
                                                })
                                              } catch (error) {
                                                toast.error(
                                                  'Failed to update role on the active timer',
                                                )
                                                return
                                              }
                                            } else {
                                              setPendingRoleByKey(prev => ({
                                                ...prev,
                                                [group.key]:
                                                  nextRoleId ?? undefined,
                                              }))
                                            }
                                          }}
                                        >
                                          <SelectTrigger className="h-7 flex-1 text-xs">
                                            <SelectValue placeholder="Select billing role" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="none">
                                              No role
                                            </SelectItem>
                                            {roles.map(role => (
                                              <SelectItem
                                                key={role.roleId}
                                                value={role.roleId}
                                              >
                                                {role.role?.name ??
                                                  'Unnamed role'}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        {!group.isCurrent &&
                                        pendingRoleByKey[group.key] ? (
                                          <span className="text-[11px] text-muted-foreground">
                                            Applies when you switch to this item
                                          </span>
                                        ) : null}
                                        {showBillingConfigurationWarnings &&
                                        !selectedRoleId ? (
                                          <div className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                                            <WarningCircleIcon
                                              className="size-3.5"
                                              weight="fill"
                                            />
                                            <span>
                                              {primaryRole?.role?.name
                                                ? PRIMARY_ROLE_FALLBACK_WARNING.replace(
                                                    'your primary role',
                                                    `your primary role (${primaryRole.role.name})`,
                                                  )
                                                : PRIMARY_ROLE_FALLBACK_WARNING}
                                            </span>
                                          </div>
                                        ) : null}
                                      </div>
                                    )
                                  })()}

                                  {linkedModuleByKey.has(group.key) ? (
                                    <TimeEntryTagging
                                      label={
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-medium text-muted-foreground">
                                            Tag members on this item
                                          </span>
                                          <InfoHint text="Only the time tracked against this item. Members tagged on all items are included automatically." />
                                        </div>
                                      }
                                      selectedUserIds={
                                        linkedModuleByKey.get(group.key)
                                          ?.taggedUserIds ?? []
                                      }
                                      onSelectedUserIdsChange={userIds =>
                                        void handleItemTagsChange(
                                          group.key,
                                          userIds,
                                        )
                                      }
                                      excludedUserIds={[activeEntry.userId]}
                                      className="w-full max-w-full"
                                      hideHelperText
                                    />
                                  ) : null}

                                  {hasExpandableLogs ? (
                                    <div>
                                      <div className="px-1.5 pb-1 text-xs font-medium text-muted-foreground">
                                        Logs
                                      </div>
                                      <div className="divide-y divide-border/40">
                                        {group.entries.map(entry => (
                                          <div
                                            key={entry.id}
                                            className="py-1.5"
                                          >
                                            <div className="flex items-center justify-between gap-3 px-1.5 text-xs">
                                              <span className="text-muted-foreground">
                                                {formatEntryWindow(entry)}
                                              </span>
                                              <span className="font-mono tabular-nums text-muted-foreground">
                                                {formatClock(
                                                  getEntryDurationSeconds(
                                                    entry,
                                                    activeEntry?.id,
                                                  ),
                                                )}
                                              </span>
                                            </div>
                                            {entry.id !== activeEntry?.id ? (
                                              <EntryDescriptionField
                                                entry={entry}
                                                onSave={description =>
                                                  handleSaveEntryDescription(
                                                    entry,
                                                    description,
                                                  )
                                                }
                                              />
                                            ) : null}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}

                                  {canRemoveInline ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 justify-start gap-1.5 px-1.5 text-xs text-muted-foreground hover:text-destructive"
                                      onClick={() =>
                                        void handleRemoveLinkedItem(group)
                                      }
                                    >
                                      <TrashIcon className="h-3.5 w-3.5" />
                                      Remove from session
                                    </Button>
                                  ) : null}
                                </div>
                              </CollapsibleContent>
                            </div>
                          </Collapsible>
                        )
                      })
                    )}
                  </div>
                </div>

                <div className="shrink-0 pt-3">
                  <TimeEntryTagging
                    selectedUserIds={sharedTaggedUserIds}
                    onSelectedUserIdsChange={userIds =>
                      void handleSharedTagsChange(userIds)
                    }
                    excludedUserIds={[activeEntry.userId]}
                    label={
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">
                          Tag members on all items
                        </span>
                        <InfoHint text="Applies to every linked item in this session. Open an item to tag someone on that item only. Tagged teammates are notified once each item's time is saved, and it appears as a suggestion on their own timesheet." />
                      </div>
                    }
                    className="w-full max-w-full"
                    hideHelperText
                  />
                </div>
              </div>

              {/* Today summary — same numbers as the dashboard Time Tracker */}
              <div className="shrink-0 border-t bg-muted/20 px-4 py-2">
                <div className="flex items-center gap-3 text-xs">
                  <span className="font-medium text-muted-foreground">
                    Today
                  </span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <ClockIcon className="h-3 w-3" />
                    <span className="font-semibold tabular-nums text-foreground">
                      {formatDuration(todayStats.totalMinutes) || '0m'}
                    </span>
                  </span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    Billable
                    <span className="font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                      {formatDuration(todayStats.billableMinutes) || '0m'}
                    </span>
                  </span>
                  <span className="text-muted-foreground">
                    {todayStats.entriesCount}{' '}
                    {todayStats.entriesCount === 1 ? 'entry' : 'entries'}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto h-6 px-2 text-xs text-primary hover:text-primary"
                    onClick={handleOpenMyTime}
                  >
                    View all
                  </Button>
                </div>
              </div>

              <div className="sticky bottom-0 z-10 shrink-0 border-t bg-background/95 px-4 py-2.5 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                <div className="flex items-center justify-between gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDiscard}
                    disabled={isDeleting}
                    className="h-8 rounded-full border-destructive/20 bg-destructive/[0.04] px-3.5 text-destructive shadow-none hover:border-destructive/30 hover:bg-destructive/[0.08] hover:text-destructive"
                  >
                    {isDeleting ? (
                      <CircleNotchIcon className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <XIcon className="mr-1 h-3.5 w-3.5" />
                    )}
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </Button>

                  <Button
                    size="sm"
                    animation="glow"
                    onClick={handleStop}
                    disabled={isStopping}
                    className="h-8 rounded-full px-4 font-semibold shadow-sm"
                  >
                    {isStopping ? (
                      <CircleNotchIcon className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <SquareIcon className="mr-1 h-3.5 w-3.5" />
                    )}
                    {isStopping ? 'Saving...' : 'End Timer'}
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </m.div>
      {splitDialog}
    </>
  )
}
